(function () {
  /**
   * upsell-cart.js v15
   *
   * Changes in this version:
   * - Applies Liquid-rendered variant inventory from #upsell-inventory-data to cart upsell products
   * - Enforces per-variant max quantity in the cart upsell UI
   * - Disables and greys out the + button at the inventory ceiling
   * - Adds stock pills to the left of the quantity selector
   * - Keeps existing masonry, instant cart sync, and panel behaviour intact
   */

  const MULTIPLIER = 1.2;
  const MAX_GROUPS = 24;
  const SHOP_ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  const DESKTOP_BP = 990;

  const LOG = false;
  const log = (...a) => { if (LOG) console.log('[UPS CART v15]', ...a); };

  let _upsellFetchWrapped = false;
  let _externalRescanTimer = null;

  function scheduleExternalRescan() {
    if (_externalRescanTimer) clearTimeout(_externalRescanTimer);
    _externalRescanTimer = setTimeout(async () => {
      try {
        await syncCartCache();
        await reconcile();
        const r = getRoot();
        if (r) syncUpsellRowsFromCart(r);
        const m = r ? r.querySelector('.upsell-masonry') : null;
        if (m) repack(m);
      } catch {}
    }, 500);
  }

  function wrapFetchForExternalCartChanges() {
    if (_upsellFetchWrapped || !window.fetch) return;
    _upsellFetchWrapped = true;

    const _origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      let url = '';
      try {
        if (typeof input === 'string') url = input;
        else if (input && typeof input.url === 'string') url = input.url;
      } catch {}

      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(url);

      let internal = false;
      try {
        const h = (init && init.headers) || (input && input.headers);
        if (h) {
          if (typeof h.get === 'function') internal = !!h.get('X-Upsell-System');
          else internal = !!(h['X-Upsell-System'] || h['x-upsell-system']);
        }
      } catch {}

      const p = _origFetch(input, init);

      if (isCartMutation && !internal) {
        Promise.resolve(p).then(() => scheduleExternalRescan()).catch(() => {});
      }

      return p;
    };
  }

  const STORAGE_KEY = 'cart_upsell_open_groups_v1';

  function readOpenState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return { open: [], closed: [], touched: false, closedAll: false, exists: false };
      const parsed = JSON.parse(raw);
      const open = Array.isArray(parsed && parsed.open) ? parsed.open.map(String) : [];
      const closed = Array.isArray(parsed && parsed.closed) ? parsed.closed.map(String) : [];
      const touched = !!(parsed && parsed.touched);
      const closedAll = !!(parsed && parsed.closedAll);
      return { open, closed, touched, closedAll, exists: true };
    } catch {
      return { open: [], closed: [], touched: false, closedAll: false, exists: false };
    }
  }

  function writeOpenState(openArr, closedArr, touched, closedAll) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        open: Array.from(new Set((openArr || []).map(String))),
        closed: Array.from(new Set((closedArr || []).map(String))),
        touched: !!touched,
        closedAll: !!closedAll
      }));
    } catch {}
  }

  function pruneOpenState(existingHandles) {
    const st = readOpenState();
    if (!st.exists) return;

    const prevOpen = Array.isArray(st.open) ? st.open.map(String) : [];
    const prevClosed = Array.isArray(st.closed) ? st.closed.map(String) : [];

    const keepOpen = prevOpen.filter(h => existingHandles.has(String(h)));
    const keepClosed = prevClosed.filter(h => existingHandles.has(String(h)));

    if (keepOpen.length !== prevOpen.length || keepClosed.length !== prevClosed.length) {
      const nextTouched = (st.touched && prevOpen.length > 0 && keepOpen.length === 0) ? false : st.touched;
      const nextClosedAll = (keepOpen.length === 0) ? (st.closedAll && nextTouched) : false;
      writeOpenState(keepOpen, keepClosed, nextTouched, nextClosedAll);
    }
  }

  function getCardHandle(card) {
    return String(card && card.getAttribute('data-upsell-handle') || '').trim();
  }

  function recordCardOpenState(card, isOpen) {
    const h = getCardHandle(card);
    if (!h) return;

    const st = readOpenState();
    const open = new Set((st.open || []).map(String));
    const closed = new Set((st.closed || []).map(String));

    if (isOpen) {
      open.add(h);
      closed.delete(h);
    } else {
      open.delete(h);
      closed.add(h);
    }

    writeOpenState(Array.from(open), Array.from(closed), true, Array.from(open).length === 0);
  }

  function applyStoredOrDefaultOpenState(masonryEl) {
    if (!masonryEl) return;

    const cards = getSortedCards(masonryEl);
    if (!cards.length) return;

    const st = readOpenState();
    const defaultCount = isDesktop() ? 2 : 1;
    const defaultHandles = cards.slice(0, defaultCount).map(getCardHandle).filter(Boolean);

    if (!st.exists) {
      openAlphabeticDefaults(masonryEl);
      const openNow = cards
        .filter(c => {
          const t = c.querySelector('input.upsell-toggle');
          return t && t.checked;
        })
        .map(getCardHandle)
        .filter(Boolean);
      writeOpenState(openNow, [], false, false);
      return;
    }

    const openSet = new Set((st.open || []).map(String));
    const closedSet = new Set((st.closed || []).map(String));

    if (!(st.touched && st.closedAll)) {
      defaultHandles.forEach(h => {
        if (!closedSet.has(h)) openSet.add(h);
      });
    }

    cards.forEach(c => {
      const h = getCardHandle(c);
      const shouldOpen = h ? (openSet.has(h) && !closedSet.has(h)) : false;
      setOpenState(c, shouldOpen);
      const panel = c.querySelector('.upsell-panel');
      const toggle = c.querySelector('input.upsell-toggle');
      if (panel && toggle) panel.style.maxHeight = toggle.checked ? (panel.scrollHeight + 'px') : '0px';
    });

    writeOpenState(Array.from(openSet), Array.from(closedSet), !!st.touched, !!(st.touched && openSet.size === 0));
  }

  function refreshOpenPanelHeights(masonryEl) {
    if (!masonryEl) return;
    const cards = masonryEl.querySelectorAll('.upsell-card');
    cards.forEach(card => {
      const toggle = card.querySelector('input.upsell-toggle');
      const panel = card.querySelector('.upsell-panel');
      if (!toggle || !panel) return;
      if (toggle.checked) panel.style.maxHeight = panel.scrollHeight + 'px';
    });
  }

  function scheduleOpenPanelHeightRefresh(masonryEl) {
    if (!masonryEl) return;
    requestAnimationFrame(() => refreshOpenPanelHeights(masonryEl));
    setTimeout(() => refreshOpenPanelHeights(masonryEl), 80);
    setTimeout(() => refreshOpenPanelHeights(masonryEl), 320);
  }

  function getRoot() { return document.getElementById('cart-upsell-root'); }
  function isCartPage() { return !!getRoot() || document.body.classList.contains('template-cart'); }
  if (!isCartPage()) return;

  function readJsonScript(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch { return null; }
  }

  function moneyFromPence(p) { return `£${(p / 100).toFixed(2)}`; }
  function multipliedFloor(p) { return Math.floor(p * MULTIPLIER); }
  function formatWithCompare(cur, comp) {
    const up = moneyFromPence(multipliedFloor(cur));
    if (comp && comp > cur) {
      const cp = moneyFromPence(multipliedFloor(comp));
      return `<s class="upsell-compare-price">${cp}</s> - ${up}`;
    }
    return `${up}`;
  }

  const inventoryScript = () => document.querySelector('#upsell-inventory-data');
  const getUpsellInventoryData = () => {
    const s = inventoryScript();
    if (!s) return {};
    try {
      const data = JSON.parse(s.textContent || '{}');
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  };

  function applyLiquidInventoryToProduct(handle, productJson) {
    if (!productJson || !Array.isArray(productJson.variants)) return productJson;

    const inventoryData = getUpsellInventoryData();
    const productInventory = inventoryData && handle ? inventoryData[String(handle)] : null;
    if (!productInventory || typeof productInventory !== 'object') return productJson;

    const patchedVariants = productJson.variants.map((variant) => {
      const meta = productInventory[String(variant.id)];
      if (!meta || typeof meta !== 'object') return variant;

      const next = { ...variant };
      if (Object.prototype.hasOwnProperty.call(meta, 'inventory_quantity')) {
        const qty = parseInt(meta.inventory_quantity, 10);
        next.inventory_quantity = Number.isNaN(qty) ? 0 : qty;
      }
      if (Object.prototype.hasOwnProperty.call(meta, 'inventory_management')) next.inventory_management = meta.inventory_management;
      if (Object.prototype.hasOwnProperty.call(meta, 'inventory_policy')) next.inventory_policy = meta.inventory_policy;
      if (Object.prototype.hasOwnProperty.call(meta, 'available')) next.available = !!meta.available;
      return next;
    });

    return { ...productJson, variants: patchedVariants };
  }

  function getStockStatus(variant) {
    const qty = hasNumericInventoryQty(variant) ? variant.inventory_quantity : (variant && variant.available ? 1 : 0);
    if (qty >= 20) return { key: 'in-stock', label: 'In stock' };
    if (qty >= 1) return { key: 'low-stock', label: 'Low stock' };
    return { key: 'sold-out', label: 'Sold out' };
  }

  function buildStockPill(variant) {
    const status = getStockStatus(variant);
    const pill = document.createElement('div');
    pill.className = `upsell-stock-pill upsell-stock-pill--${status.key}`;
    pill.textContent = status.label;
    return pill;
  }

  function ensureVariantMetaWrapStyles() {
    const STYLE_ID = 'upsell-cart-variant-meta-wrap';
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .upsell-masonry .upsell-text {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 2px !important;
      }
      .upsell-masonry .upsell-title-meta {
        margin-left: 0 !important;
      }
      .upsell-masonry .upsell-title-meta .upsell-compare-price,
      .upsell-masonry .upsell-title-meta s {
        color: rgb(105, 105, 105) !important;
      }
    `;
    document.head.appendChild(style);
  }

  function parseVariantIdsCsv(raw) {
    if (!raw) return null;
    const matches = String(raw).match(/\d{6,}/g);
    if (!matches || !matches.length) return null;
    return new Set(matches.map(id => String(id)));
  }

  function getAllowedSet(rule) {
    if (rule && rule.all_variants !== true && rule.allowed_variant_ids_csv && String(rule.allowed_variant_ids_csv).trim().length) {
      return parseVariantIdsCsv(rule.allowed_variant_ids_csv);
    }
    return null;
  }

  function hasNumericInventoryQty(v) {
    return typeof v.inventory_quantity === 'number' && !Number.isNaN(v.inventory_quantity);
  }

  function isVariantSelectable(v) {
    if (hasNumericInventoryQty(v)) return v.inventory_quantity > 0;
    return !!v.available;
  }

  function shouldHideUpsellCompletely(rule, productJson) {
    const allowed = getAllowedSet(rule);
    const variants = Array.isArray(productJson && productJson.variants) ? productJson.variants : [];
    const relevant = allowed ? variants.filter(v => allowed.has(String(v.id))) : variants;
    if (!relevant.length) return true;

    const withQty = relevant.filter(hasNumericInventoryQty);
    if (withQty.length) return withQty.every(v => v.inventory_quantity <= 0);
    return relevant.every(v => !v.available);
  }

  function normaliseImgSrc(src) {
    if (!src) return '';
    return String(src).replace(/^\/\//, 'https://');
  }

  function variantThumbSrc(productJson, variant) {
    const vimg = variant && variant.featured_image && variant.featured_image.src ? variant.featured_image.src : '';
    if (vimg) return normaliseImgSrc(vimg);
    if (productJson && productJson.featured_image) return normaliseImgSrc(productJson.featured_image);
    const imgs = productJson && Array.isArray(productJson.images) ? productJson.images : [];
    if (imgs.length) return normaliseImgSrc(imgs[0]);
    return '';
  }

  function normaliseStep(rule) {
    const s = parseInt((rule && rule.qty_step) || '1', 10);
    if (s === 2 || s === 4) return s;
    return 1;
  }

  function applyLabelTemplate(tpl, title) {
    return String(tpl || '')
      .replace(/\[\[\s*upsell_title\s*\]\]/g, title)
      .replace(/{{\s*upsell_title\s*}}/g, title);
  }

  function sortKeyFromText(t) {
    return String(t || '').trim().toUpperCase().replace(/\s+/g, ' ');
  }

  async function fetchCart() {
    const r = await fetch(`${SHOP_ROOT}cart.js`, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) throw new Error(`cart.js failed: ${r.status}`);
    return await r.json();
  }

  async function addVariant(variantId, qty) {
    const res = await fetch(`${SHOP_ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Upsell-System': '1' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(qty) }] })
    });
    if (!res.ok) throw new Error(`cart/add.js failed: ${res.status}`);
    return await res.json();
  }

  async function changeLineByKey(key, qty) {
    const res = await fetch(`${SHOP_ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Upsell-System': '1' },
      body: JSON.stringify({ id: key, quantity: Number(qty) })
    });
    if (!res.ok) throw new Error(`cart/change.js failed: ${res.status}`);
    return await res.json();
  }

  function buildCartIndex(cart) {
    const idx = new Map();
    (cart.items || []).forEach(it => {
      if (!it || !it.variant_id) return;
      idx.set(String(it.variant_id), { key: it.key, quantity: it.quantity || 0, handle: it.handle || '' });
    });
    return idx;
  }

  async function refreshCartSections() {
    const ids = ['main-cart-items', 'main-cart-footer'];
    const existing = ids.filter(id => document.getElementById(`shopify-section-${id}`));
    if (!existing.length) return false;

    const url = `${window.location.pathname}?sections=${encodeURIComponent(existing.join(','))}`;
    const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) return false;

    const data = await r.json();
    existing.forEach(id => {
      const el = document.getElementById(`shopify-section-${id}`);
      if (el && typeof data[id] === 'string') el.innerHTML = data[id];
    });

    return true;
  }

  const productCache = new Map();
  const tagsCache = new Map();

  async function fetchProduct(handle) {
    const h = String(handle || '').trim();
    if (!h) throw new Error('Missing handle');
    if (productCache.has(h)) return productCache.get(h);
    const r = await fetch(`/products/${h}.js`, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) throw new Error(`products/${h}.js failed`);
    const j = await r.json();
    const patched = applyLiquidInventoryToProduct(h, j);
    productCache.set(h, patched);
    tagsCache.set(h, new Set((patched.tags || []).map(String)));
    return patched;
  }

  function ensureStyles() {
    if (document.getElementById('cart-upsell-styles')) return;
    const s = document.createElement('style');
    s.id = 'cart-upsell-styles';
    s.textContent = `
      #cart-upsell-root { margin-top: 6px; }
      #cart-upsell-root .cart-upsell-title {
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: .06em;
        margin: 0 0 10px;
        color: #303030;
      }

      #cart-upsell-root .upsell-masonry { display: block; }
      #cart-upsell-root .upsell-cols {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        align-items: start;
      }
      @media (min-width: ${DESKTOP_BP}px) {
        #cart-upsell-root .upsell-cols { grid-template-columns: 1fr 1fr; }
      }
      #cart-upsell-root .upsell-col { display: grid; gap: 12px; align-content: start; }

      #cart-upsell-root .upsell-card {
        background: #fff;
        border: 1px solid #e6e6e6;
        border-radius: 10px;
        overflow: hidden;
      }

      #cart-upsell-root .upsell-card__head {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: #f4f4f4;
        border-bottom: 1px solid #e6e6e6;
        cursor: pointer;
        user-select: none;
      }

      #cart-upsell-root .upsell-card__title {
        margin: 0;
        font-size: 11px;
        line-height: 1.2;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: .06em;
        color: #303030;
      }

      #cart-upsell-root .upsell-switch { display: inline-grid; place-items: center; position: relative; }
      #cart-upsell-root .upsell-switch input { position: absolute; opacity: 0; width: 1px; height: 1px; overflow: hidden; }
      #cart-upsell-root .upsell-switch .track {
        width: 42px; height: 22px; border-radius: 999px; background: #3A3A3A;
        box-shadow: inset 0 0 0 1px rgba(0,0,0,0.10);
        position: relative; transition: background-color .18s ease, box-shadow .18s ease;
      }
      #cart-upsell-root .upsell-switch .thumb {
        width: 18px; height: 18px; border-radius: 999px; background: #fff;
        position: absolute; top: 2px; left: 2px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
        transition: transform .18s ease;
      }
      #cart-upsell-root .upsell-switch input:checked + .track { background: #FDAE08; }
      #cart-upsell-root .upsell-switch input:checked + .track .thumb { transform: translateX(20px); }

      #cart-upsell-root .upsell-panel {
        overflow: hidden;
        max-height: 0;
        transition: max-height 360ms cubic-bezier(.2,.9,.2,1);
        will-change: max-height;
      }

      #cart-upsell-root.is-restoring .upsell-panel { transition: none !important; }
      #cart-upsell-root .upsell-list { list-style: none; padding: 0; margin: 0; }

      #cart-upsell-root .upsell-row {
        display: grid;
        grid-template-columns: 44px minmax(0, 1fr) auto 90px 28px;
        align-items: center;
        column-gap: 10px;
        padding: 10px 12px;
      }
      #cart-upsell-root .upsell-row + .upsell-row { border-top: 1px solid #eeeeee; }

      #cart-upsell-root .upsell-thumb {
        width: 44px; height: 44px; border-radius: 8px; overflow: hidden;
        background: #f6f6f6; border: 1px solid #eeeeee; display: grid; place-items: center;
      }
      #cart-upsell-root .upsell-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
      #cart-upsell-root .upsell-row.is-disabled .upsell-thumb img { opacity: 0.5; }

      #cart-upsell-root .upsell-text {
        font-size: 14px; line-height: 1.25; font-weight: 800; color: #1a1a1a;
        text-transform: uppercase; letter-spacing: .02em; min-width: 0;
      }
      #cart-upsell-root .upsell-text.is-disabled { color: #9ca3af; }

      #cart-upsell-root .upsell-stock-pill {
        justify-self: end;
        align-self: center;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 56px;
        height: 36px;
        padding: 0 12px;
        border-radius: 999px;
        font-size: 9px;
        line-height: 1;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: .06em;
        white-space: nowrap;
        box-sizing: border-box;
      }
      #cart-upsell-root .upsell-stock-pill--in-stock {
        color: #303030;
        background: #FDAE08;
      }
      #cart-upsell-root .upsell-stock-pill--low-stock {
        color: #ffffff;
        background: #ff2d2d;
      }
      #cart-upsell-root .upsell-stock-pill--sold-out {
        color: #b8b8b8;
        background: #e1e1e1;
      }

      #cart-upsell-root .upsell-qty {
        justify-self: end;
        width: 90px;
        height: 36px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #fff;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        overflow: hidden;
        box-shadow: 0 1px 2px rgba(17,24,39,0.04);
      }
      #cart-upsell-root .upsell-qty button {
        appearance: none; border: 0; background: transparent;
        font-size: 17px; font-weight: 800; line-height: 1;
        cursor: pointer; padding: 0; display: grid; place-items: center; color: #111827;
      }
      #cart-upsell-root .upsell-qty .qty-val {
        display: grid; place-items: center; font-size: 15px; font-weight: 800; color: #111827;
      }
      #cart-upsell-root .upsell-qty button:disabled { cursor: not-allowed; color: #9ca3af; }
      #cart-upsell-root .upsell-qty.is-disabled { opacity: 0.6; }

      #cart-upsell-root .upsell-check { display: grid; place-items: center; }
      #cart-upsell-root .upsell-check input[type="checkbox"]{
        -webkit-appearance: none; appearance: none;
        width: 22px; height: 22px; border-radius: 6px;
        border: 1px solid #d1d5db; background: #fff;
        display: inline-grid; place-content: center; cursor: pointer;
        transition: border-color .12s, box-shadow .12s, background-color .12s;
      }
      #cart-upsell-root .upsell-check input[type="checkbox"]::before{
        content: ""; width: 12px; height: 12px; transform: scale(0);
        transition: transform .12s; box-shadow: inset 1em 1em #111827; border-radius: 3px;
      }
      #cart-upsell-root .upsell-check input[type="checkbox"]:checked{
        border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,.10);
      }
      #cart-upsell-root .upsell-check input[type="checkbox"]:checked::before{ transform: scale(1); }
      #cart-upsell-root .upsell-check input[type="checkbox"]:disabled{
        cursor: not-allowed; background: #f3f4f6; border-color: #e5e7eb;
      }
      #cart-upsell-root .upsell-check input[type="checkbox"]:disabled::before{
        box-shadow: inset 1em 1em #9ca3af;
      }

      #cart-upsell-root .upsell-row.is-updating { opacity: .65; pointer-events: none; }
      #cart-upsell-root .upsell-card.is-updating { opacity: .85; pointer-events: none; }

      @media (max-width: 480px) {
        #cart-upsell-root .upsell-row {
          grid-template-columns: 44px minmax(0, 1fr) auto 84px 28px;
        }
        #cart-upsell-root .upsell-stock-pill {
          min-width: 56px;
          height: 36px;
          padding: 0 12px;
          font-size: 9px;
        }
        #cart-upsell-root .upsell-qty { width: 84px; height: 34px; border-radius: 11px; }
        #cart-upsell-root .upsell-qty button { font-size: 16px; }
        #cart-upsell-root .upsell-qty .qty-val { font-size: 14px; }
      }

      @media (prefers-reduced-motion: reduce) {
        #cart-upsell-root .upsell-panel,
        #cart-upsell-root .upsell-switch .track,
        #cart-upsell-root .upsell-switch .thumb { transition: none; }
      }
    `;
    document.head.appendChild(s);
  }

  function isDesktop() { return window.matchMedia(`(min-width: ${DESKTOP_BP}px)`).matches; }

  function buildColumns() {
    const wrap = document.createElement('div');
    wrap.className = 'upsell-cols';

    const colA = document.createElement('div');
    colA.className = 'upsell-col';
    colA.setAttribute('data-col', 'a');

    const colB = document.createElement('div');
    colB.className = 'upsell-col';
    colB.setAttribute('data-col', 'b');

    wrap.appendChild(colA);
    wrap.appendChild(colB);

    return { wrap, colA, colB };
  }

  function getSortedCards(masonryEl) {
    const cards = Array.from(masonryEl.querySelectorAll('.upsell-card'));
    cards.sort((a, b) => {
      const ka = a.getAttribute('data-sort') || '';
      const kb = b.getAttribute('data-sort') || '';
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      return 0;
    });
    return cards;
  }

  function packCardsIntoColumnsFixedHeads(masonryEl) {
    const cols = masonryEl.querySelectorAll('.upsell-col');
    if (cols.length !== 2) return;

    const [colA, colB] = cols;
    const cards = getSortedCards(masonryEl);
    if (!cards.length) return;

    const first = cards[0] || null;
    const second = cards[1] || null;
    const rest = cards.slice(2);

    const fragA = document.createDocumentFragment();
    const fragB = document.createDocumentFragment();

    let hA = 0;
    let hB = 0;

    if (first) {
      fragA.appendChild(first);
      hA += first.getBoundingClientRect().height;
    }
    if (second) {
      fragB.appendChild(second);
      hB += second.getBoundingClientRect().height;
    }

    rest.forEach(card => {
      const ch = card.getBoundingClientRect().height;
      if (hA <= hB) {
        fragA.appendChild(card);
        hA += ch;
      } else {
        fragB.appendChild(card);
        hB += ch;
      }
    });

    colA.innerHTML = '';
    colB.innerHTML = '';
    colA.appendChild(fragA);
    colB.appendChild(fragB);
  }

  function repack(masonryEl) {
    if (!masonryEl) return;

    const cols = masonryEl.querySelectorAll('.upsell-col');
    if (cols.length !== 2) return;

    const [colA, colB] = cols;

    if (!isDesktop()) {
      const cards = getSortedCards(masonryEl);
      colA.innerHTML = '';
      cards.forEach(c => colA.appendChild(c));
      colB.style.display = 'none';
      return;
    }

    colB.style.display = '';
    requestAnimationFrame(() => packCardsIntoColumnsFixedHeads(masonryEl));
  }

  function openPanel(card) {
    const panel = card.querySelector('.upsell-panel');
    const toggle = card.querySelector('input.upsell-toggle');
    const head = card.querySelector('.upsell-card__head');
    if (!panel || !toggle || !head) return;
    toggle.checked = true;
    head.setAttribute('aria-expanded', 'true');
    panel.style.maxHeight = panel.scrollHeight + 'px';
  }

  function closePanel(card) {
    const panel = card.querySelector('.upsell-panel');
    const toggle = card.querySelector('input.upsell-toggle');
    const head = card.querySelector('.upsell-card__head');
    if (!panel || !toggle || !head) return;
    toggle.checked = false;
    head.setAttribute('aria-expanded', 'false');
    panel.style.maxHeight = '0px';
  }

  function setOpenState(card, open) { open ? openPanel(card) : closePanel(card); }

  function openAlphabeticDefaults(masonryEl) {
    if (!masonryEl) return;

    const cards = getSortedCards(masonryEl);
    if (!cards.length) return;

    cards.forEach(c => setOpenState(c, false));

    if (!isDesktop()) {
      setOpenState(cards[0], true);
      return;
    }

    setOpenState(cards[0], true);
    if (cards[1]) setOpenState(cards[1], true);
  }

  function bindCollapsibles(root, masonryEl) {
    const cards = Array.from(root.querySelectorAll('.upsell-card'));
    cards.forEach((card) => {
      const head = card.querySelector('.upsell-card__head');
      const toggle = card.querySelector('input.upsell-toggle');
      const panel = card.querySelector('.upsell-panel');
      const track = card.querySelector('.upsell-switch .track');

      if (!head || !toggle || !panel) return;

      head.addEventListener('click', () => {
        const nextOpen = !toggle.checked;
        setOpenState(card, nextOpen);
        recordCardOpenState(card, nextOpen);
        setTimeout(() => repack(masonryEl), 50);
        setTimeout(() => repack(masonryEl), 420);
      });

      if (track) {
        track.addEventListener('click', (e) => {
          e.stopPropagation();
          const nextOpen = !toggle.checked;
          setOpenState(card, nextOpen);
          recordCardOpenState(card, nextOpen);
          setTimeout(() => repack(masonryEl), 50);
          setTimeout(() => repack(masonryEl), 420);
        });
      }

      toggle.addEventListener('click', (e) => { e.stopPropagation(); });

      const imgs = Array.from(panel.querySelectorAll('img'));
      imgs.forEach(img => {
        img.addEventListener('load', () => {
          if (toggle.checked) panel.style.maxHeight = panel.scrollHeight + 'px';
          repack(masonryEl);
        }, { once: true });
      });
    });

    window.addEventListener('resize', () => {
      repack(masonryEl);
      setTimeout(() => {
        const st = readOpenState();
        const anyOpen = !!masonryEl.querySelector('.upsell-card input.upsell-toggle:checked');

        if (!anyOpen) {
          if (!st.exists) openAlphabeticDefaults(masonryEl);
          else if (!st.touched) openAlphabeticDefaults(masonryEl);
        }

        if (!st.exists) {
          const cards = getSortedCards(masonryEl);
          const openNow = cards
            .filter(c => {
              const t = c.querySelector('input.upsell-toggle');
              return t && t.checked;
            })
            .map(getCardHandle)
            .filter(Boolean);
          writeOpenState(openNow, [], false, false);
        }
      }, 60);
    });
  }

  function getStepForRow(row) {
    const s = parseInt(row.getAttribute('data-upsell-step') || '1', 10);
    return (!Number.isNaN(s) && s > 0) ? s : 1;
  }

  function getMaxQtyForRow(row) {
    const raw = row.getAttribute('data-upsell-max-qty');
    if (!raw || raw === 'Infinity') return Infinity;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? Infinity : Math.max(0, parsed);
  }

  function syncQtyControls(row) {
    const qty = parseInt(row.getAttribute('data-upsell-qty') || '0', 10) || 0;
    const max = getMaxQtyForRow(row);

    const minus = row.querySelector('button[data-qty-action="minus"]');
    if (minus) minus.disabled = (qty <= 0);

    const plus = row.querySelector('button[data-qty-action="plus"]');
    const atMax = Number.isFinite(max) ? qty >= max : false;
    if (plus) {
      plus.disabled = atMax || plus.disabled;
      plus.setAttribute('aria-disabled', (atMax || plus.disabled) ? 'true' : 'false');
    }
  }

  function setRowQtyUI(row, qty) {
    const max = getMaxQtyForRow(row);
    let q = Math.max(0, parseInt(qty || '0', 10) || 0);
    if (Number.isFinite(max)) q = Math.min(q, max);

    row.setAttribute('data-upsell-qty', String(q));

    const val = row.querySelector('.upsell-qty .qty-val');
    if (val) val.textContent = String(q);

    const cb = row.querySelector('input.upsell-checkbox');
    if (cb && !cb.disabled) cb.checked = (q >= 1);

    syncQtyControls(row);
  }

  function ruleMatchesCart(rule, cartHandlesSet, cartTagsSet) {
    const tt = String(rule.trigger_type || '').toLowerCase();
    if (tt === 'product') {
      const h = String(rule.trigger_product_handle || '').trim();
      if (!h) return false;
      return cartHandlesSet.has(h);
    }
    if (tt === 'tag') {
      const tag = String(rule.trigger_tag || '').trim();
      if (!tag) return false;
      return cartTagsSet.has(tag);
    }
    return false;
  }

  let cartCache = null;
  let cartIndex = null;

  const cartHandlesSet = new Set();
  const cartTagsSet = new Set();

  let renderedHandles = [];
  let busy = false;

  async function syncCartCache() {
    cartCache = await fetchCart();
    cartIndex = buildCartIndex(cartCache);

    cartHandlesSet.clear();
    (cartCache.items || []).forEach(it => {
      const h = String(it.handle || '').trim();
      if (h) cartHandlesSet.add(h);
    });

    const newHandles = Array.from(cartHandlesSet).filter(h => !tagsCache.has(h));
    await Promise.all(newHandles.map(async (h) => {
      try { await fetchProduct(h); } catch {}
    }));

    cartTagsSet.clear();
    cartHandlesSet.forEach(h => {
      const set = tagsCache.get(h);
      if (set) set.forEach(t => cartTagsSet.add(t));
    });

    return cartCache;
  }

  function buildUpsellCard(rule, productJson, idx) {
    const card = document.createElement('div');
    card.className = 'upsell-card';
    card.setAttribute('data-upsell-handle', String(rule.upsell_handle || ''));

    const head = document.createElement('div');
    head.className = 'upsell-card__head';
    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    head.setAttribute('aria-expanded', 'false');

    const headerText = applyLabelTemplate(rule.custom_label || 'Add [[upsell_title]]', productJson.title);

    const title = document.createElement('h3');
    title.className = 'upsell-card__title';
    title.textContent = headerText;

    card.setAttribute('data-sort', sortKeyFromText(headerText));

    const switchWrap = document.createElement('div');
    switchWrap.className = 'upsell-switch';

    const toggleId = `cart-upsell-toggle-${idx}`;
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.className = 'upsell-toggle';
    toggle.id = toggleId;

    const track = document.createElement('span');
    track.className = 'track';
    const thumb = document.createElement('span');
    thumb.className = 'thumb';
    track.appendChild(thumb);

    const toggleLabel = document.createElement('label');
    toggleLabel.className = 'visually-hidden';
    toggleLabel.setAttribute('for', toggleId);
    toggleLabel.textContent = 'Toggle upsell section';

    switchWrap.appendChild(toggle);
    switchWrap.appendChild(track);
    switchWrap.appendChild(toggleLabel);

    head.appendChild(title);
    head.appendChild(switchWrap);

    const panel = document.createElement('div');
    panel.className = 'upsell-panel';
    panel.style.maxHeight = '0px';

    const list = document.createElement('ul');
    list.className = 'upsell-list';

    const step = normaliseStep(rule);
    const allowed = getAllowedSet(rule);
    const isDefaultTitle = (t) => /^default\s+title$/i.test(String(t || '').trim());
    const stripLeadingAdd = (t) => String(t || '').replace(/^\s*add\s+/i, '').trim();

    const variants = Array.isArray(productJson.variants) ? productJson.variants : [];
    const relevantVariants = allowed ? variants.filter(v => allowed.has(String(v.id))) : variants;

    relevantVariants.forEach((v, rowIndex) => {
      const selectable = isVariantSelectable(v);

      const li = document.createElement('li');
      li.className = 'upsell-row' + (selectable ? '' : ' is-disabled');
      li.setAttribute('data-upsell-step', String(step));
      li.setAttribute('data-upsell-variant-id', String(v.id));
      li.setAttribute('data-upsell-qty', '0');
      li.setAttribute('data-upsell-max-qty', hasNumericInventoryQty(v) ? String(Math.max(0, v.inventory_quantity)) : 'Infinity');

      const thumbWrap = document.createElement('div');
      thumbWrap.className = 'upsell-thumb';
      const src = variantThumbSrc(productJson, v);
      if (src) {
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = '';
        img.src = src;
        thumbWrap.appendChild(img);
      }
      li.appendChild(thumbWrap);

      const baseName = isDefaultTitle(v.title) ? productJson.title : v.title;
      const variantName = stripLeadingAdd(baseName);
      const pretty = formatWithCompare(v.price, v.compare_at_price);
      const full = `${variantName} ${pretty}`;

      const text = document.createElement('div');
      text.className = 'upsell-text' + (selectable ? '' : ' is-disabled');

      const mainSpan = document.createElement('span');
      mainSpan.className = 'upsell-title-main';
      mainSpan.textContent = variantName;

      const metaSpan = document.createElement('span');
      metaSpan.className = 'upsell-title-meta';
      metaSpan.innerHTML = pretty;

      text.appendChild(mainSpan);
      text.appendChild(metaSpan);
      li.appendChild(text);

      const stockPill = buildStockPill(v);
      li.appendChild(stockPill);

      const qty = document.createElement('div');
      qty.className = 'upsell-qty' + (selectable ? '' : ' is-disabled');

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.setAttribute('data-qty-action', 'minus');
      minus.textContent = '−';

      const val = document.createElement('div');
      val.className = 'qty-val';
      val.textContent = '0';

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.setAttribute('data-qty-action', 'plus');
      plus.textContent = '+';

      qty.appendChild(minus);
      qty.appendChild(val);
      qty.appendChild(plus);
      li.appendChild(qty);

      const checkWrap = document.createElement('div');
      checkWrap.className = 'upsell-check';

      const inputId = `cart-upsell-check-${idx}-${rowIndex}`;

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = inputId;
      cb.className = 'upsell-checkbox';
      cb.disabled = !selectable;

      const sr = document.createElement('label');
      sr.className = 'visually-hidden';
      sr.setAttribute('for', inputId);
      sr.textContent = full;

      checkWrap.appendChild(cb);
      checkWrap.appendChild(sr);
      li.appendChild(checkWrap);

      list.appendChild(li);

      if (!selectable) {
        minus.disabled = true;
        plus.disabled = true;
        plus.setAttribute('aria-disabled', 'true');
      } else {
        minus.disabled = true;
        plus.setAttribute('aria-disabled', 'false');
      }

      syncQtyControls(li);
    });

    const hasSelectable = Array.from(list.querySelectorAll('input.upsell-checkbox')).some(i => !i.disabled);
    if (!hasSelectable) return null;

    panel.appendChild(list);
    card.appendChild(head);
    card.appendChild(panel);

    return card;
  }

  function syncUpsellRowsFromCart(root) {
    if (!cartIndex) return;
    const rows = Array.from(root.querySelectorAll('.upsell-row'));
    rows.forEach(row => {
      const vid = row.getAttribute('data-upsell-variant-id');
      const item = cartIndex.get(String(vid));
      const q = item ? (item.quantity || 0) : 0;
      setRowQtyUI(row, q);
    });
  }

  async function computeDesiredRuleHandles(allRules) {
    const desired = [];
    allRules.forEach(r => {
      if (!r || !r.upsell_handle) return;
      if (ruleMatchesCart(r, cartHandlesSet, cartTagsSet)) desired.push(String(r.upsell_handle));
    });
    const seen = new Set();
    return desired.filter(h => (seen.has(h) ? false : (seen.add(h), true)));
  }

  function sameOrder(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  async function applyCartTargetQty(variantId, targetQty) {
    if (!cartIndex) await syncCartCache();

    const key = String(variantId);
    const cur = cartIndex.get(key);
    const currentQty = cur ? (cur.quantity || 0) : 0;

    const target = Math.max(0, parseInt(targetQty || '0', 10) || 0);
    if (target === currentQty) return;

    if (target > currentQty) {
      await addVariant(variantId, target - currentQty);
    } else {
      if (!cur || !cur.key) {
        await syncCartCache();
        return;
      }
      await changeLineByKey(cur.key, target);
    }
  }

  async function reconcile() {
    const root = getRoot();
    if (!root) return;

    const rulesPayload = readJsonScript('upsell-rules-data') || {};
    const allRules = Array.isArray(rulesPayload.all_rules) ? rulesPayload.all_rules : [];
    if (!allRules.length) {
      root.innerHTML = '';
      renderedHandles = [];
      return;
    }

    const desiredHandles = (await computeDesiredRuleHandles(allRules)).slice(0, MAX_GROUPS);
    const masonryExisting = root.querySelector('.upsell-masonry');

    if (masonryExisting && sameOrder(renderedHandles, desiredHandles)) return;

    root.innerHTML = '';
    renderedHandles = desiredHandles.slice();

    if (!renderedHandles.length) return;

    const title = document.createElement('p');
    title.className = 'cart-upsell-title';
    title.textContent = 'Recommended add-ons';
    root.appendChild(title);

    const masonry = document.createElement('div');
    masonry.className = 'upsell-masonry';
    root.appendChild(masonry);

    const { wrap } = buildColumns();
    masonry.appendChild(wrap);

    const rulesByHandle = new Map();
    allRules.forEach(r => {
      if (!r || !r.upsell_handle) return;
      const h = String(r.upsell_handle);
      if (!rulesByHandle.has(h) && ruleMatchesCart(r, cartHandlesSet, cartTagsSet)) rulesByHandle.set(h, r);
    });

    const built = [];
    let idx = 1;

    for (const handle of renderedHandles) {
      const rule = rulesByHandle.get(handle);
      if (!rule) continue;

      try {
        const pjson = await fetchProduct(handle);
        if (shouldHideUpsellCompletely(rule, pjson)) continue;

        const card = buildUpsellCard(rule, pjson, idx++);
        if (!card) continue;

        built.push(card);
      } catch (e) {
        log('Failed to build upsell', handle, e);
      }
    }

    if (!built.length) {
      root.innerHTML = '';
      renderedHandles = [];
      return;
    }

    built.sort((a, b) => {
      const ka = a.getAttribute('data-sort') || '';
      const kb = b.getAttribute('data-sort') || '';
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      return 0;
    });

    const colA = wrap.querySelector('.upsell-col[data-col="a"]');
    built.forEach(c => colA.appendChild(c));

    bindCollapsibles(root, masonry);
    bindInstantInteractions(root);

    syncUpsellRowsFromCart(root);

    const existingHandles = new Set(built.map(c => getCardHandle(c)).filter(Boolean));
    pruneOpenState(existingHandles);

    root.classList.add('is-restoring');
    masonry.style.visibility = 'hidden';

    setTimeout(() => {
      repack(masonry);

      requestAnimationFrame(() => {
        applyStoredOrDefaultOpenState(masonry);
        scheduleOpenPanelHeightRefresh(masonry);

        requestAnimationFrame(() => {
          masonry.style.visibility = '';
          scheduleOpenPanelHeightRefresh(masonry);
          root.classList.remove('is-restoring');

          setTimeout(() => repack(masonry), 60);
          setTimeout(() => repack(masonry), 420);
        });
      });
    }, 0);
  }

  async function doInstantUpdate(row, targetQty) {
    if (busy) return;
    busy = true;

    const root = getRoot();
    const card = row.closest('.upsell-card');
    const masonry = root ? root.querySelector('.upsell-masonry') : null;

    row.classList.add('is-updating');
    if (card) card.classList.add('is-updating');

    const vid = row.getAttribute('data-upsell-variant-id');
    const max = getMaxQtyForRow(row);
    const safeTarget = Number.isFinite(max) ? Math.min(Math.max(0, targetQty), max) : Math.max(0, targetQty);

    try {
      await applyCartTargetQty(vid, safeTarget);

      const ok = await refreshCartSections();
      if (!ok) {
        window.location.reload();
        return;
      }

      await syncCartCache();
      await reconcile();

      const r = getRoot();
      if (r) syncUpsellRowsFromCart(r);

      if (masonry) repack(masonry);
    } catch (e) {
      log('Instant update failed', e);
      try {
        await syncCartCache();
        await reconcile();
        const r = getRoot();
        if (r) syncUpsellRowsFromCart(r);
        if (masonry) repack(masonry);
      } catch {}
    } finally {
      busy = false;
      row.classList.remove('is-updating');
      if (card) card.classList.remove('is-updating');
    }
  }

  function bindInstantInteractions(root) {
    root.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('button[data-qty-action]') : null;
      if (!btn) return;

      const row = btn.closest('.upsell-row');
      if (!row) return;

      const cb = row.querySelector('input.upsell-checkbox');
      if (cb && cb.disabled) return;

      const act = btn.getAttribute('data-qty-action');
      const step = getStepForRow(row);
      const cur = parseInt(row.getAttribute('data-upsell-qty') || '0', 10) || 0;
      const max = getMaxQtyForRow(row);

      if (act === 'plus') {
        const next = Number.isFinite(max) ? Math.min(cur + step, max) : (cur + step);
        setRowQtyUI(row, next);
        doInstantUpdate(row, next);
      }

      if (act === 'minus') {
        const next = Math.max(0, cur - step);
        setRowQtyUI(row, next);
        doInstantUpdate(row, next);
      }

      e.preventDefault();
      e.stopPropagation();
    });

    root.addEventListener('change', (e) => {
      const cb = e.target && e.target.matches && e.target.matches('input.upsell-checkbox[type="checkbox"]') ? e.target : null;
      if (!cb) return;

      const row = cb.closest('.upsell-row');
      if (!row) return;

      const step = getStepForRow(row);
      const cur = parseInt(row.getAttribute('data-upsell-qty') || '0', 10) || 0;
      const max = getMaxQtyForRow(row);

      if (cb.checked) {
        const nextBase = (cur >= 1) ? cur : step;
        const next = Number.isFinite(max) ? Math.min(nextBase, max) : nextBase;
        setRowQtyUI(row, next);
        doInstantUpdate(row, next);
      } else {
        setRowQtyUI(row, 0);
        doInstantUpdate(row, 0);
      }
    });
  }

  async function init() {
    const root = getRoot();
    if (!root) return;

    ensureStyles();
    ensureVariantMetaWrapStyles();

    wrapFetchForExternalCartChanges();

    window.addEventListener('resize', () => {
      const r = getRoot();
      const mEl = r ? r.querySelector('.upsell-masonry') : null;
      if (mEl) scheduleOpenPanelHeightRefresh(mEl);
    });

    await syncCartCache();
    await reconcile();

    document.addEventListener('change', async (e) => {
      const t = e.target;
      if (!t) return;

      const isCartQty = t.matches && (t.matches('input[name="updates[]"]') || t.matches('quantity-input input') || t.matches('.quantity__input'));
      if (!isCartQty) return;

      setTimeout(async () => {
        try {
          await syncCartCache();
          await reconcile();
          const r = getRoot();
          if (r) syncUpsellRowsFromCart(r);
          const m = r ? r.querySelector('.upsell-masonry') : null;
          if (m) repack(m);
        } catch {}
      }, 600);
    }, true);
  }

  document.addEventListener('DOMContentLoaded', () => { init(); });
  document.addEventListener('shopify:section:load', () => { init(); });
})();